import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["data-index"]

import type { VirtualItem } from '@tanstack/vue-virtual';
import { useVirtualizer } from '@tanstack/vue-virtual';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { getOverflowAncestors } from '@floating-ui/dom';
import { useMutationObserver } from '@vueuse/core';

// Component interface
type Props = {
  items: Items;
  pageMode?: boolean;
  activeIndex?: number | null;
  gap?: number;
  horizontal?: boolean;
  scrollToActiveIndex?: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'base-list',
  props: {
    items: {},
    pageMode: { type: Boolean },
    activeIndex: { default: null },
    gap: { default: 8 },
    horizontal: { type: Boolean, default: false },
    scrollToActiveIndex: { type: Boolean, default: true }
  },
  emits: ["scroll"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



// Emits
const emit = __emit;



const onChange = ({ scrollOffset }: { scrollOffset: number | null }) => {
  emit('scroll', scrollOffset);
};

// Virtual List setup
const parentRef = useTemplateRef<HTMLElement>('parentRef');
const scrollMargin = ref(0);

const scrollableRoot = computed(() => {
  const container = parentRef.value;
  if (__props.pageMode && container) {
    const list = getOverflowAncestors(container) as HTMLElement[];
    return list[0];
  }
  return container;
});
useMutationObserver(scrollableRoot, () => {
  if (parentRef.value && parentRef.value.offsetParent !== null) {
    scrollMargin.value = __props.pageMode ? parentRef.value.offsetTop : 0;
  }
}, {
  childList: true,
  subtree: true
});

// В pageMode при пересоздании списка меняется высота страницы из за чего может скрол дёрнуть вверх
// Это что бы такого не было, бага была замечена при использовании с табами
if (__props.pageMode) {
  watch(scrollableRoot, (el) => {
    if (!el) {
      return;
    }
    const savedTop = el.scrollTop;
    requestAnimationFrame(() => {
      if (el.scrollTop !== savedTop) {
        el.scrollTop = savedTop;
      }
    });
  });
}

const virtualOptions = computed(() => ({
  count: __props.items.length,
  getScrollElement: () => scrollableRoot.value,
  horizontal: __props.horizontal,
  overscan: 2,
  scrollMargin: scrollMargin.value,
  estimateSize: () => 60,
  gap: __props.gap,
  onChange
}));

const listVirtualizer = useVirtualizer(virtualOptions);

const virtualItems = computed(() => listVirtualizer.value.getVirtualItems());
const virtualIndexSet = computed(() => new Set(virtualItems.value.map((vi) => vi.index)));
const totalSize = computed(() => listVirtualizer.value.getTotalSize());
const measureElement = (el) => {
  if (!el) {
    return;
  }
  listVirtualizer.value.measureElement(el);
  return undefined;
};

// Styles
const listStyle = computed(() => {
  const h = __props.horizontal;
  const height = h ? '100%' : `${totalSize.value}px`;
  const width = h ? `${totalSize.value}px` : '100%';
  const transform = `translate${h ? 'X' : 'Y'}(${(virtualItems.value[0]?.start - scrollMargin.value)}px)`;

  return {
    list: {
      height,
      width
    },
    virtual: {
      transform,
      gap: `${__props.gap}px`,
      width: h ? '' : '100%',
      height: h ? '100%' : '',
      flexDirection: h ? 'row' : 'column'
    }
  };
});

// Functions
const scrollToIndex = (index: number, smooth?: boolean) => {
  listVirtualizer.value.scrollToIndex(index, {
    align: 'center',
    behavior: smooth ? 'smooth' : 'auto'
  });
};

const scrollToTop = (smooth?: boolean) => {
  listVirtualizer.value.scrollToOffset(0, { behavior: smooth ? 'smooth' : 'auto' });
};
const scrollToBottom = (smooth?: boolean) => {
  listVirtualizer.value.scrollToOffset(totalSize.value, { behavior: smooth ? 'smooth' : 'auto' });
};

watch(() => __props.activeIndex, (index) => {
  if (index === null || !__props.scrollToActiveIndex) {
    return;
  }
  scrollToIndex(index);
});

onMounted(() => {
  setTimeout(() => {
    if (__props.activeIndex === null) {
      return;
    }
    scrollToIndex(__props.activeIndex);
  }, 0);
});

__expose({
  scrollToIndex,
  scrollToTop,
  scrollToBottom
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "parentRef",
    ref: parentRef,
    class: _normalizeClass([_ctx.$style.container, { [_ctx.$style.pageMode]: __props.pageMode }]),
    style: _normalizeStyle({ gridAutoFlow: __props.horizontal ? 'column' : 'row' })
  }, [
    _renderSlot(_ctx.$slots, "before"),
    _createElementVNode("div", {
      class: _normalizeClass(_ctx.$style.list),
      style: _normalizeStyle(listStyle.value.list)
    }, [
      _createElementVNode("ul", {
        class: _normalizeClass(_ctx.$style.virtualList),
        style: _normalizeStyle(listStyle.value.virtual)
      }, [
        _renderSlot(_ctx.$slots, "virtualItems", {
          virtualItems: virtualItems.value,
          virtualIndexSet: virtualIndexSet.value,
          measure: measureElement
        }, () => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(virtualItems.value, (virtualItem) => {
            return (_openBlock(), _createElementBlock("li", {
              key: virtualItem.key as string,
              ref_for: true,
              ref: measureElement,
              class: _normalizeClass(_ctx.$style.virtualItem),
              "data-index": virtualItem.index
            }, [
              _renderSlot(_ctx.$slots, "item", {
                index: virtualItem.index,
                item: __props.items[virtualItem.index]
              })
            ], 10, _hoisted_1))
          }), 128))
        ])
      ], 6)
    ], 6),
    _renderSlot(_ctx.$slots, "after")
  ], 6))
}
}

})